import React, { useState } from "react";
import { Tab, Tabs, styled, Grid, Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import PieChart from "./components/PieChart";
import { ContainerWithLoader, SelectByDateWithCustomPeriod } from "../../../../components/ui";
import BitPayChargesUSvsInternational from "../BitPayChargesUSvsInternational";
import { getReportValues } from "./utils";
import { DATES_VALUE } from "../../../../constants";
import { ContainerBg } from "../../../../useGeneralStyles";
import { APIBilling } from "../../../../api";
import PaymentProvidersData from "./components/PaymentProvidersData/index";
import { INITIAL_REPORT_VALUES, NET_SALES_CALCULATOR_TAB_INDEXES } from "./constants";
import { BROKER_TAB } from "../../constants";

const StyledTab = styled((props: { value: string; label: string }) => <Tab {...props} />)(() => ({
  padding: "3px 6px",
  minWidth: "auto",
  minHeight: 38,
}));

const NetSalesCalculator = (): React.ReactElement => {
  const [report, setReport] = useState(INITIAL_REPORT_VALUES);
  const [activeTabIndex, setActiveTab] = useState(NET_SALES_CALCULATOR_TAB_INDEXES.combined_sales);
  const [activeBroker, setActiveBroker] = useState(BROKER_TAB.combined);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isAllTime, setIsAllTime] = useState(false);

  const isCombinedSales = activeTabIndex === "combined_sales";

  const { us_total = 0, international_total = 0 } = getReportValues(report, activeTabIndex);

  const onChangeActiveTab = (_: React.BaseSyntheticEvent, i: NET_SALES_CALCULATOR_TAB_INDEXES) =>
    setActiveTab(i);
  const onChangeBrokerTab = (_: React.BaseSyntheticEvent, i: BROKER_TAB) => setActiveBroker(i);

  const getReport = useQuery({
    enabled: !!startDate || !!endDate,
    queryKey: [startDate, endDate, isAllTime, activeTabIndex, activeBroker],
    queryFn: async ({ signal }) => {
      const response = await APIBilling.getNetSalesValuesRequest(
        startDate,
        endDate,
        activeTabIndex,
        signal,
        activeBroker
      );
      setReport(response.data.report);
      return response;
    },
  });

  return (
    <div>
      <h2>Net sales calculator</h2>
      <SelectByDateWithCustomPeriod
        setIsLoading={() => void null}
        getReport={(startDate, endDate, isAllTime) => {
          setStartDate(startDate);
          setEndDate(endDate);
          setIsAllTime(Boolean(isAllTime));
        }}
        setReport={setReport}
        excludeDates={[DATES_VALUE.allTIme]}
      />

      <ContainerBg sx={{ paddingTop: 0 }}>
        <Tabs value={activeBroker} onChange={onChangeBrokerTab} centered sx={{ mb: 1 }}>
          <StyledTab value={BROKER_TAB.combined} label="Combined" />
          <StyledTab value={BROKER_TAB.tradovate} label="Tradovate" />
          <StyledTab value={BROKER_TAB.rithmic} label="Rithmic" />
        </Tabs>
        <Tabs value={activeTabIndex} onChange={onChangeActiveTab} centered>
          <StyledTab
            value={NET_SALES_CALCULATOR_TAB_INDEXES.combined_sales}
            label="Combined sales"
          />
          <StyledTab value={NET_SALES_CALCULATOR_TAB_INDEXES.us_sales} label="US sales" />
          <StyledTab
            value={NET_SALES_CALCULATOR_TAB_INDEXES.international_sales}
            label="international sales"
          />
        </Tabs>

        <ContainerWithLoader
          isLoading={getReport.isLoading || getReport.isFetching}
          report={[report]}
        >
          <Grid container>
            <Grid item xs={4}>
              <PaymentProvidersData report={report} activeTabIndex={activeTabIndex} />
            </Grid>

            <Grid item xs={4}>
              {isCombinedSales && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <PieChart us_total={us_total} international_total={international_total} />
                </Box>
              )}
            </Grid>
            <Grid item xs={4}>
              <BitPayChargesUSvsInternational
                not_us_cryptocurrency={report.not_us_cryptocurrency}
                us_cryptocurrency={report.us_cryptocurrency}
              />
            </Grid>
          </Grid>
        </ContainerWithLoader>
      </ContainerBg>
    </div>
  );
};

export default NetSalesCalculator;
